import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Reveal } from './animations';
import AppNav from './AppNav';
import ForumAttachment from './ForumAttachment';
import { useProfile } from './ProfileContext';
import { supabase } from './supabaseClient';
import ALL_OCCUPATIONS from './data/allMilitaryOccupations';
import './Card.css';
import './ForumPage.css';

const BRANCHES = ['U.S. Army', 'U.S. Marine Corps', 'U.S. Navy', 'U.S. Air Force', 'U.S. Space Force', 'U.S. Coast Guard'];
const ALL_BRANCHES = 'all';
const BRANCH_LOGOS = {
  'U.S. Army':         '/500px-Mark_of_the_United_States_Army.svg.png',
  'U.S. Marine Corps': '/500px-Emblem_of_the_United_States_Marine_Corps.svg.png',
  'U.S. Navy':         '/500px-Emblem_of_the_United_States_Navy.svg.png',
  'U.S. Air Force':    '/500px-U.S._Air_Force_service_mark.svg.png',
  'U.S. Space Force':  '/500px-Seal_of_the_United_States_Space_Force.svg.png',
  'U.S. Coast Guard':  '/500px-Seal_of_the_United_States_Coast_Guard.svg.png',
};
const TOPICS = ['VA Benefits', 'Cybersecurity Jobs', 'PCS Questions'];
const ALLOWED_ATTACHMENT_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

function ReplyIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" {...props}>
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  );
}

function AttachIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" {...props}>
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-8.49 8.48a2 2 0 01-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

function shortBranch(fullBranch) {
  return (fullBranch || '').replace('U.S. ', '');
}

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function hotScore(voteCount, createdAt) {
  const ageHours = (Date.now() - new Date(createdAt)) / 3600000;
  return voteCount / Math.pow(ageHours + 2, 1.5);
}

function sortPosts(rows, mode) {
  const sorted = [...rows];
  if (mode === 'top') sorted.sort((a, b) => b.voteCount - a.voteCount || new Date(b.created_at) - new Date(a.created_at));
  else if (mode === 'hot') sorted.sort((a, b) => hotScore(b.voteCount, b.created_at) - hotScore(a.voteCount, a.created_at));
  else sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  return sorted;
}

function occupationsForBranch(fullBranch) {
  const short = shortBranch(fullBranch);
  const seen = new Set();
  return ALL_OCCUPATIONS.filter(o => o.branch === short).filter(o => {
    if (seen.has(o.code)) return false;
    seen.add(o.code);
    return true;
  });
}

export default function ForumPage() {
  const { profile } = useProfile();
  const [userId,       setUserId]       = useState(null);
  const [activeBranch, setActiveBranch] = useState(null);
  const [afscFilter,   setAfscFilter]   = useState('all');
  const [posts,        setPosts]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [refreshKey,   setRefreshKey]   = useState(0);
  const [composeOpen,  setComposeOpen]  = useState(false);
  const [composeTitle, setComposeTitle] = useState('');
  const [composeBody,  setComposeBody]  = useState('');
  const [composeAfsc,  setComposeAfsc]  = useState('none');
  const [composeFile,  setComposeFile]  = useState(null);
  const [attachError,  setAttachError]  = useState('');
  const [submitting,   setSubmitting]   = useState(false);
  const [sortMode,     setSortMode]     = useState('new');

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) setUserId(session.user.id);
    });
  }, []);

  // Default to the signed-in veteran's own branch once it's loaded.
  useEffect(() => {
    if (!activeBranch && profile.branch) setActiveBranch(profile.branch);
  }, [profile.branch, activeBranch]);

  const branchOccupations = useMemo(() => occupationsForBranch(activeBranch), [activeBranch]);
  const myOccupations = useMemo(() => occupationsForBranch(profile.branch), [profile.branch]);

  useEffect(() => {
    if (!activeBranch) return;
    setAfscFilter('all');
  }, [activeBranch]);

  useEffect(() => {
    if (!activeBranch) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      let query = supabase
        .from('forum_posts')
        .select('id, author_id, branch, topic, occupation_code, occupation_title, title, body, created_at, attachment_url, attachment_name, attachment_type, forum_replies(count), forum_post_votes(count)')
        .order('created_at', { ascending: false });

      if (activeBranch === ALL_BRANCHES) {
        query = query.not('branch', 'is', null);
      } else if (TOPICS.includes(activeBranch)) {
        query = query.eq('topic', activeBranch);
      } else {
        query = query.eq('branch', activeBranch);
        if (afscFilter !== 'all') query = query.eq('occupation_code', afscFilter);
      }

      const { data: postsData } = await query;
      if (cancelled) return;

      const rows = postsData || [];
      const authorIds = [...new Set(rows.map(p => p.author_id))];
      const authorMap = {};
      if (authorIds.length > 0) {
        const { data: authors } = await supabase
          .from('profiles_public')
          .select('id, first_name, last_name, avatar_url, branch')
          .in('id', authorIds);
        (authors || []).forEach(a => { authorMap[a.id] = a; });
      }

      let myVoteIds = new Set();
      if (userId && rows.length > 0) {
        const { data: myVotes } = await supabase
          .from('forum_post_votes')
          .select('post_id')
          .eq('user_id', userId)
          .in('post_id', rows.map(p => p.id));
        myVoteIds = new Set((myVotes || []).map(v => v.post_id));
      }

      const withVotes = rows.map(p => ({
        ...p,
        author: authorMap[p.author_id] || null,
        replyCount: p.forum_replies?.[0]?.count || 0,
        voteCount: p.forum_post_votes?.[0]?.count || 0,
        hasVoted: myVoteIds.has(p.id),
      }));
      setPosts(sortPosts(withVotes, sortMode));
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [activeBranch, afscFilter, refreshKey, userId, sortMode]);

  const handleToggleVote = async (post) => {
    if (!userId) return;
    setPosts(prev => sortPosts(prev.map(p => p.id === post.id
      ? { ...p, hasVoted: !p.hasVoted, voteCount: p.voteCount + (p.hasVoted ? -1 : 1) }
      : p
    ), sortMode));

    if (post.hasVoted) {
      await supabase.from('forum_post_votes').delete().eq('post_id', post.id).eq('user_id', userId);
    } else {
      await supabase.from('forum_post_votes').insert({ post_id: post.id, user_id: userId });
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
      setAttachError('Only images (JPEG, PNG, GIF, WebP) or PDF files are supported.');
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setAttachError('File is too large — 5MB max.');
      return;
    }
    setAttachError('');
    setComposeFile(file);
  };

  const resetCompose = () => {
    setComposeOpen(false);
    setComposeTitle('');
    setComposeBody('');
    setComposeAfsc('none');
    setComposeFile(null);
    setAttachError('');
  };

  const composingTopic = TOPICS.includes(activeBranch) ? activeBranch : null;

  const handleCreatePost = async () => {
    if (!userId || !composeTitle.trim() || !composeBody.trim() || submitting) return;
    setSubmitting(true);
    setAttachError('');

    const occ = composeAfsc !== 'none' ? myOccupations.find(o => o.code === composeAfsc) : null;

    let attachment_url = null;
    let attachment_name = null;
    let attachment_type = null;

    if (composeFile) {
      const safeName = composeFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `${userId}/${Date.now()}-${safeName}`;
      const { error: uploadError } = await supabase.storage
        .from('forum-attachments')
        .upload(path, composeFile, { cacheControl: '0' });

      if (uploadError) {
        console.error('Attachment upload failed:', uploadError);
        setAttachError('Upload failed — try again.');
        setSubmitting(false);
        return;
      }

      attachment_url = path;
      attachment_name = composeFile.name;
      attachment_type = composeFile.type;
    }

    const { error } = await supabase.from('forum_posts').insert({
      author_id: userId,
      branch: composingTopic ? null : profile.branch,
      topic: composingTopic,
      occupation_code: occ?.code || null,
      occupation_title: occ?.title || null,
      title: composeTitle.trim(),
      body: composeBody.trim(),
      attachment_url,
      attachment_name,
      attachment_type,
    });

    setSubmitting(false);
    if (error) { setAttachError('Your post was not saved. Please try again.'); return; }

    const postedIn = composingTopic || profile.branch;
    resetCompose();
    setActiveBranch(postedIn);
    setAfscFilter('all');
    setRefreshKey(k => k + 1);
  };

  return (
    <div className="forum-root vq-surface">
      <AppNav />
      <main className="forum-shell">
        <header className="forum-page-heading">
          <svg className="forum-heading-mark" viewBox="0 0 40 48" fill="none" aria-hidden="true">
            <path d="M3 3h34v24L20 44 3 27V3Z" stroke="currentColor" strokeWidth="1.5" />
            <path d="m11 18 9-6 9 6m-18 8 9-6 9 6" stroke="currentColor" strokeWidth="1.5" />
          </svg>
          <div>
            <p className="forum-eyebrow">The veteran community</p>
            <h1>Across branches. Among peers.</h1>
            <p>Share what you know. Ask what comes next. Help another veteran forward.</p>
          </div>
        </header>
        <Reveal>
          <div className="forum-branch-tabs">
            <button
              className={`forum-branch-tab forum-branch-tab-all ${activeBranch === ALL_BRANCHES ? 'active' : ''}`}
              aria-pressed={activeBranch === ALL_BRANCHES}
              onClick={() => setActiveBranch(ALL_BRANCHES)}
            >
              <span className="forum-branch-tab-label">All Branches</span>
            </button>
            {BRANCHES.map(b => (
              <button
                key={b}
                className={`forum-branch-tab ${activeBranch === b ? 'active' : ''}`}
                data-branch={b}
                aria-pressed={activeBranch === b}
                onClick={() => setActiveBranch(b)}
              >
                <span className="forum-branch-tab-logo">
                  <img src={BRANCH_LOGOS[b]} alt="" />
                </span>
                <span className="forum-branch-tab-label">{shortBranch(b)}</span>
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.03}>
          <div className="forum-topic-tabs">
            {TOPICS.map(t => (
              <button
                key={t}
                className={`forum-topic-tab ${activeBranch === t ? 'active' : ''}`}
                aria-pressed={activeBranch === t}
                onClick={() => setActiveBranch(t)}
              >
                {t}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="forum-toolbar">
            <div className="forum-toolbar-left">
              {!TOPICS.includes(activeBranch) && activeBranch !== ALL_BRANCHES && (
                <select aria-label="Filter by military occupation" className="forum-afsc-select" value={afscFilter} onChange={e => setAfscFilter(e.target.value)}>
                  <option value="all">All occupations</option>
                  {branchOccupations.map(o => (
                    <option key={o.code} value={o.code}>{o.code} — {o.title}</option>
                  ))}
                </select>
              )}
              <div className="forum-sort-tabs">
                {['new', 'hot', 'top'].map(m => (
                  <button
                    key={m}
                    className={`forum-sort-tab ${sortMode === m ? 'active' : ''}`}
                    aria-pressed={sortMode === m}
                    onClick={() => setSortMode(m)}
                  >
                    {m === 'new' ? 'New' : m === 'hot' ? 'Hot' : 'Top'}
                  </button>
                ))}
              </div>
            </div>
            {(activeBranch === profile.branch || TOPICS.includes(activeBranch)) && (
              <button className="forum-new-post-btn" onClick={() => setComposeOpen(true)}>+ New Post</button>
            )}
          </div>
        </Reveal>

        {loading ? (
          <div className="forum-loading vq-card" role="status">
            <div className="forum-spinner" />
            <p>Loading discussions from your veteran community…</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="forum-empty vq-card">
            <div className="forum-empty-icon"><ReplyIcon width="28" height="28" /></div>
            {activeBranch === ALL_BRANCHES ? (
              <>
                <h3>No posts yet</h3>
                <p>Be the first to start a discussion — pick your branch above to post.</p>
              </>
            ) : TOPICS.includes(activeBranch) ? (
              <>
                <h3>No posts yet in {activeBranch}</h3>
                <p>Be the first to start a discussion — open to every branch.</p>
              </>
            ) : (
              <>
                <h3>No posts yet in {shortBranch(activeBranch)}</h3>
                <p>{activeBranch === profile.branch ? 'Start a post to share experience or ask your fellow veterans a question.' : 'Discussions from this branch will appear here. Choose your own branch or a shared topic to start a post.'}</p>
              </>
            )}
          </div>
        ) : (
          <div className="forum-post-list">
            {posts.map((p, i) => {
              const initials = `${p.author?.first_name?.[0] || ''}${p.author?.last_name?.[0] || ''}`.toUpperCase() || '?';
              return (
                <Reveal key={p.id} delay={Math.min(i * 0.04, 0.3)}>
                  <Link to={`/forums/${p.id}`} className="forum-post-card vq-card" data-branch={p.branch || p.author?.branch}>
                    <button
                      className={`forum-vote-btn ${p.hasVoted ? 'active' : ''}`}
                      onClick={e => { e.preventDefault(); e.stopPropagation(); handleToggleVote(p); }}
                    >
                      <span className="forum-vote-arrow">▲</span>
                      <span className="forum-vote-count">{p.voteCount}</span>
                    </button>
                    <div className="forum-post-av">
                      {p.author?.avatar_url
                        ? <img src={p.author.avatar_url} alt="avatar" className="forum-post-av-img" />
                        : initials
                      }
                    </div>
                    <div className="forum-post-body">
                      <div className="forum-post-meta">
                        <span className="forum-post-author">{p.author ? `${p.author.first_name} ${p.author.last_name}` : 'Member'}</span>
                        {activeBranch === ALL_BRANCHES && (
                          <span className="forum-post-branch-badge">
                            <img src={BRANCH_LOGOS[p.branch]} alt="" className="forum-post-branch-badge-logo" />
                            {shortBranch(p.branch)}
                          </span>
                        )}
                        {TOPICS.includes(activeBranch) && p.author?.branch && (
                          <span className="forum-post-branch-badge">
                            <img src={BRANCH_LOGOS[p.author.branch]} alt="" className="forum-post-branch-badge-logo" />
                            {shortBranch(p.author.branch)}
                          </span>
                        )}
                        {p.occupation_code && <span className="forum-post-afsc">{p.occupation_code}</span>}
                        <span className="forum-post-time">{timeAgo(p.created_at)}</span>
                      </div>
                      <div className="forum-post-title">{p.title}</div>
                      {p.attachment_url && p.attachment_type?.startsWith('image/') && (
                        <ForumAttachment value={p.attachment_url} name={p.attachment_name} type={p.attachment_type} thumbnail />
                      )}
                      {p.attachment_url && p.attachment_type === 'application/pdf' && (
                        <div className="forum-post-file-chip"><AttachIcon /> {p.attachment_name}</div>
                      )}
                      <div className="forum-post-replies"><ReplyIcon /> {p.replyCount} {p.replyCount === 1 ? 'reply' : 'replies'}</div>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        )}
      </main>

      {composeOpen && (
        <div className="forum-compose-overlay" onClick={e => { if (e.target === e.currentTarget) resetCompose(); }}>
          <div className="forum-compose-modal">
            <div className="forum-compose-header">
              <h3>
                {!composingTopic && (
                  <img src={BRANCH_LOGOS[profile.branch]} alt="" className="forum-compose-header-logo" />
                )}
                New Post — {composingTopic || shortBranch(profile.branch)}
              </h3>
              <button className="forum-compose-close" aria-label="Close post composer" onClick={resetCompose}>×</button>
            </div>
            <label className="forum-compose-label">Title</label>
            <input
              className="forum-compose-input"
              value={composeTitle}
              onChange={e => setComposeTitle(e.target.value)}
              placeholder="What do you want to ask or share?"
              maxLength={150}
            />
            <label className="forum-compose-label">Job (optional)</label>
            <select className="forum-compose-input" value={composeAfsc} onChange={e => setComposeAfsc(e.target.value)}>
              <option value="none">No specific job</option>
              {myOccupations.map(o => (
                <option key={o.code} value={o.code}>{o.code} — {o.title}</option>
              ))}
            </select>
            <label className="forum-compose-label">Post</label>
            <textarea
              className="forum-compose-textarea"
              value={composeBody}
              onChange={e => setComposeBody(e.target.value)}
              placeholder="Share details, ask your question…"
              rows={5}
            />
            <label className="forum-compose-label">Attachment (optional)</label>
            {composeFile ? (
              <div className="forum-attach-preview">
                {composeFile.type.startsWith('image/')
                  ? <img src={URL.createObjectURL(composeFile)} alt="preview" className="forum-attach-preview-img" />
                  : <span className="forum-post-file-chip"><AttachIcon /> {composeFile.name}</span>
                }
                <button type="button" className="forum-attach-remove" onClick={() => setComposeFile(null)}>Remove</button>
              </div>
            ) : (
              <input type="file" accept={ALLOWED_ATTACHMENT_TYPES.join(',')} onChange={handleFileSelect} className="forum-compose-file-input" />
            )}
            <p className="forum-attach-hint">Posts and attachments are shared with approved members. Recipients can save copies. Do not include service records, medical information, CUI, or sensitive mission details.</p>
            {attachError && <p className="forum-attach-error">{attachError}</p>}
            <button
              className="forum-new-post-btn forum-compose-submit"
              onClick={handleCreatePost}
              disabled={!composeTitle.trim() || !composeBody.trim() || submitting}
            >
              {submitting ? 'Posting…' : 'Post'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
